import {controller, attr} from '@github/catalyst'

@controller
class PasskeyRegistrationElement extends HTMLElement {
  @attr registrationUrl: string = ''

  async startRegistration() {
    try {
      // Get registration options from server
      const optionsResponse = await fetch(this.registrationUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfToken
        }
      })

      if (!optionsResponse.ok) {
        throw new Error('Failed to get registration options')
      }

      const options = await optionsResponse.json()

      // Convert challenge and user ID from Base64
      options.challenge = this.base64ToArrayBuffer(options.challenge)
      options.user.id = this.base64ToArrayBuffer(options.user.id)
      if (options.excludeCredentials) {
        options.excludeCredentials = options.excludeCredentials.map((c: {id: string, type: string}) => ({
          ...c,
          id: this.base64ToArrayBuffer(c.id)
        }))
      }

      // Create credential via WebAuthn API
      const credential = await navigator.credentials.create({
        publicKey: options
      }) as PublicKeyCredential

      if (!credential) {
        throw new Error('Failed to create credential')
      }

      const response = credential.response as AuthenticatorAttestationResponse

      // Prompt for nickname
      const nickname = prompt('Give this passkey a name (e.g., "MacBook Touch ID", "YubiKey"):')

      // Send credential to server - preserve query params (like setup=true)
      const url = new URL(this.registrationUrl, window.location.origin)
      url.pathname = url.pathname.replace('/new', '')
      const registerResponse = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({
          id: credential.id,
          rawId: this.arrayBufferToBase64(credential.rawId),
          type: credential.type,
          response: {
            attestationObject: this.arrayBufferToBase64(response.attestationObject),
            clientDataJSON: this.arrayBufferToBase64(response.clientDataJSON)
          },
          nickname: nickname || undefined
        })
      })

      const result = await registerResponse.json()

      if (result.success) {
        // For setup flow, redirect to security settings; otherwise reload current page
        const urlParams = new URLSearchParams(url.search)
        if (urlParams.get('setup') === 'true') {
          window.location.href = '/settings/security'
        } else {
          window.location.reload()
        }
      } else {
        alert(result.error || 'Registration failed')
      }
    } catch (error) {
      console.error('Passkey registration error:', error)
      if ((error as Error).name === 'NotAllowedError') {
        alert('Registration was cancelled.')
      } else {
        alert('Failed to register passkey. Please try again.')
      }
    }
  }

  private get csrfToken(): string {
    return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content || ''
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64.replace(/-/g, '+').replace(/_/g, '/'))
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    return bytes.buffer
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer)
    let binary = ''
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i])
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
  }
}
